import {
  ASSISTANT_API_PATH,
  ASSISTANT_PROXY_PATH,
  assistantGatewayUrl,
} from "@/components/AssistantPanel/assistant-endpoint";
import { chargeCodeApi, namingChargeCodeApi } from "@/config/charge-code-api";
import { EnvVariables } from "@/env-variables";
import { envStorage } from "@/lib/storage/envIsolatedStorage";

/**
 * Route Configuration
 * Maps request paths to:
 * 1. An internal service path (rewrite)
 * 2. A specific base URL (domain)
 */
interface RouteConfig {
  path: string; // Internal service path
  baseUrl?: string; // Target domain (if different from default VITE_API_URL)
}

const getApiVersion = (): string => {
  const stored = envStorage.getItem("debug_api_version");
  return stored === "v2" ? "v1" : "v1";
};

const getRouteMappings = (): Record<string, RouteConfig> => {
  const version = getApiVersion();

  return {
    "/api/patient": { path: `/patient-management-service/api/${version}` },
    "/api/outpatient": { path: `/outpatient-visit-service/api/${version}` },
    "/api/auth": { path: `/auth-service/api/${version}` },
    "/api/staff-management": {
      path: `/api/staff-management/${version}`,
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/staffs": { path: "/api/staffs", baseUrl: EnvVariables.apiUrlDocuments },
    "/api/inpatient-billings": {
      path: "/api/inpatient-billings",
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/procedure": { path: `/outpatient-procedure-service/api/${version}` },
    [ASSISTANT_PROXY_PATH]: {
      path: ASSISTANT_API_PATH,
      baseUrl: assistantGatewayUrl(EnvVariables.apiUrlAssistant),
    },
    // erp-integration mounts its own ROUTE_PREFIX ahead of the version segment, and pins v1
    // internally, so this one does not follow the /<service>/api/<version> shape.
    "/api/erp": {
      path: "/api/erp-service/api/v1",
      baseUrl: EnvVariables.apiUrlOrganization,
    },
    "/api/billing": { path: `/outpatient-billing-service/api/${version}` },
    "/api/aggregator": { path: `/echo-aggregator-service/api/${version}` },
    "/api/appointments": {
      path: `/api/echo/v2/appointments`,
      baseUrl: EnvVariables.apiUrlAppointments,
    },
    "/api/block-time-slots": {
      path: "/api/block-time-slots",
      baseUrl: EnvVariables.apiUrlBlockTimeSlots,
    },
    "/api/time-slots": {
      path: "/api/time-slots",
      baseUrl: EnvVariables.apiUrlAppointments,
    },
    "/api/reminder-template": {
      path: "/api/appointments/reminder-template",
      baseUrl: EnvVariables.apiUrlApptSmsReminder,
    },
    "/api/trigger-sms-reminders": {
      path: "/api/appointments/trigger-sms-reminders",
      baseUrl: EnvVariables.apiUrlApptSmsReminderTrigger,
    },
    "/api/reports": { path: `/report-service/api/${version}` },
    "/api/organisations": {
      path: "/api/organisations",
      baseUrl: EnvVariables.apiUrlOrganization,
    },
    "/api/documents": {
      path: "/api/documents",
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/document-groups": {
      path: "/api/document-groups",
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/queue": { path: `/queue-management-service/api/${version}` },
    "/api/clinic": { path: `/clinic-service/api/${version}` },
    "/api/doctors": { path: "/api/doctors", baseUrl: EnvVariables.apiUrlPartner },
    "/api/locations": { path: "/api/locations", baseUrl: EnvVariables.apiUrlPartner },
    // Role management is served exclusively through the MS30 Echo Aggregator
    // (/api/aggregator/roles, /api/aggregator/system-modules). The frontend must not
    // call the role-management service directly, so no direct route is mapped here.
    "/api/users": { path: "/api/users", baseUrl: EnvVariables.apiUrlDocuments },
    "/api/terminal-management": {
      path: "/api/terminal-management",
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/specialties": {
      path: "/api/specialties",
      baseUrl: EnvVariables.apiUrlDocuments,
    },
    "/api/master-datas": {
      path: "/api/master-datas",
      baseUrl: EnvVariables.apiUrlPartner,
    },
  };
};

/**
 * Rewrites the relative URL for the development environment, keeping it relative so the Vite proxy
 * intercepts it. Visit and billing URLs gain a v2 segment after their prefix when the user chose v2,
 * which the proxy's rewrite turns into `api/v1/v2`; every other URL is left as it is.
 *
 * @param relativeUrl The original relative URL (e.g., /api/outpatient/visits)
 * @returns The URL the Vite proxy forwards (e.g., /api/outpatient/v2/visits)
 */
export const rewriteRelativePathVersion = (relativeUrl: string): string =>
  relativeUrl ? namingChargeCodeApi(relativeUrl, chargeCodeApi()) : relativeUrl;

/**
 * Rewrites the API path and resolves the absolute URL based on the route configuration.
 * Used for non-development environments to bypass Nginx and hit the correct Microservice/Gateway directly.
 *
 * @param relativeUrl The original relative URL (e.g., /api/patient/list)
 * @returns The fully qualified absolute URL (e.g., https://api.gateway.com/dev/patient-management-service/api/v1/list)
 */
export const rewriteAbsolutePath = (relativeUrl: string): string => {
  if (!relativeUrl) return relativeUrl;

  // If URL is already absolute (http://, https://, protocol-relative), keep it as-is.
  // This prevents accidental concatenation with default API base URL.
  if (/^([a-z][a-z\d+\-.]*:)?\/\//i.test(relativeUrl)) {
    return relativeUrl;
  }

  // Default Base URL
  const defaultBaseUrl = EnvVariables.apiUrl;
  const routeMappings = getRouteMappings();
  const url = namingChargeCodeApi(relativeUrl, chargeCodeApi());

  for (const [prefix, config] of Object.entries(routeMappings)) {
    if (url.startsWith(prefix)) {
      // Determine the correct Base URL (Domain)
      const targetBaseUrl = config.baseUrl || defaultBaseUrl;

      // Determine the correct Path (Rewrite)
      const targetPath = url.replace(prefix, config.path);

      // Combine them
      // Ensure no double slashes if baseURL ends with / and path starts with /
      const cleanBase = targetBaseUrl.replace(/\/+$/, "");
      const cleanPath = targetPath.replace(/^\/+/, "");

      return `${cleanBase}/${cleanPath}`;
    }
  }

  // Fallback: Use default API URL + original path
  const cleanDefaultBase = defaultBaseUrl.replace(/\/+$/, "");
  const cleanOriginalPath = url.replace(/^\/+/, "");
  return `${cleanDefaultBase}/${cleanOriginalPath}`;
};
